/**
 * Enhanced Mobile Menu with Tabbed Accordion Structure
 * Feature-flagged implementation for smoother, more organized mobile navigation
 * Preserves all SEO/LLM optimization with shadow navigation
 */
'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { LPThemeToggle } from '@/components/v2/LPThemeToggle';
import Image from 'next/image';
import { FreeToolsShowcase } from './FreeToolsShowcase';
import { TryItLink } from './TryItLink';
import logo from '@/assets/leadpops-mortgage-marketing-logo.webp';
import { 
  ChevronDown, 
  ChevronLeft,
  Globe,
  FileText,
  Search,
  Mail,
  Gauge,
  DollarSign,
  Smartphone, 
  Video, 
  PenTool, 
  MapPin, 
  QrCode, 
  Home, 
  Send, 
  Users, 
  Palette, 
  Rocket,
  ArrowRight,
  Monitor,
  Target,
  Bot,
  User,
  Building,
  Shield,
  TrendingUp,
  Star,
  Briefcase,
  Diamond,
  GraduationCap,
  Lightbulb,
  Library
} from '@/lib/icons';

interface NavigationItem {
  label: string;
  href: string;
  type?: 'mega';
  dropdown?: {
    label: string;
    href: string;
    description: string;
  }[];
  columns?: any[];
  cta?: any;
  desktopOnly?: boolean;
}

interface MobileMenuProps {
  items: NavigationItem[];
}

// Icon mapping
const iconMap = {
  'Globe': Globe,
  'FileText': FileText,
  'Search': Search,
  'Mail': Mail,
  'Gauge': Gauge,
  'DollarSign': DollarSign,
  'Smartphone': Smartphone,
  'Video': Video,
  'PenTool': PenTool,
  'MapPin': MapPin,
  'QrCode': QrCode,
  'Home': Home,
  'Send': Send,
  'Users': Users,
  'Palette': Palette,
  'Rocket': Rocket,
  'ArrowRight': ArrowRight,
  'Monitor': Monitor,
  'Target': Target,
  'Bot': Bot,
  'User': User,
  'Building': Building,
  'Shield': Shield,
  'TrendingUp': TrendingUp,
  'Star': Star,
  'Briefcase': Briefcase,
  'Diamond': Diamond,
  'GraduationCap': GraduationCap,
  'Lightbulb': Lightbulb,
  'Library': Library
};

// Premium tools configuration

export function MobileMenu({ items, startHere = false }: MobileMenuProps & { /** The Start Here page: the menu's key opens the chooser. */ startHere?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    // the open menu sits above the sitewide widgets (social-proof toast): see the SITEWIDE block in skin-game-css.ts
    document.documentElement.toggleAttribute('data-menu-open', isOpen);

    // Lock body scroll when menu is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      
      // Check if we need compact mode based on viewport height
      // Compact mode for screens shorter than 700px
      const checkHeight = () => {
        const vh = window.innerHeight;
        setIsCompact(vh < 700);
      };
      
      checkHeight();
      window.addEventListener('resize', checkHeight);
      
      return () => {
        window.removeEventListener('resize', checkHeight);
        document.body.style.overflow = '';
      };
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  const openMegaMenu = (label: string) => {
    setIsAnimating(true);
    setTimeout(() => {
      setActiveMegaMenu(label);
      setExpandedSection(null); // Reset expanded section
      setIsAnimating(false);
    }, 150);
  };

  const closeMegaMenu = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setActiveMegaMenu(null);
      setExpandedSection(null);
      setIsAnimating(false);
    }, 150);
  };

  const toggleSection = (title: string) => {
    setExpandedSection(expandedSection === title ? null : title);
  };

  const closeMenu = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsOpen(false);
      setActiveMegaMenu(null);
      setExpandedSection(null);
      setIsAnimating(false);
    }, 200);
  };

  return (
    <div className="xl:hidden">
      
      {/* Enhanced Hamburger Button with Animation */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative inline-flex items-center justify-center p-2 rounded-md text-neutral-700 hover:text-primary hover:bg-gray-100 transition-all duration-300"
        aria-label="Toggle navigation menu"
      >
        <div className="relative w-6 h-6 flex flex-col justify-center items-end">
          {/* Animated hamburger lines - tighter spacing, middle line shorter */}
          <span 
            className={`block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out ${
              isOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-1'
            }`}
          />
          <span 
            className={`block h-0.5 bg-current transform transition-all duration-300 ease-in-out my-0.5 ${
              isOpen ? 'opacity-0 w-6' : 'opacity-100 w-5'
            }`}
          />
          <span 
            className={`block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out ${
              isOpen ? '-rotate-45 -translate-y-0.5' : 'translate-y-1'
            }`}
          />
        </div>
      </button>

      {/* Enhanced Mobile Menu with Smooth Animations */}
      <div 
        className={`lp-mm fixed inset-0 bg-white z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <Link href="/" onClick={closeMenu}>
              <Image
                src={logo}
                alt="LeadPops Mortgage Lead Generation Platform Logo"
                width={140}
                height={51}
                className="h-8 w-auto"
              />
            </Link>
            <LPThemeToggle className="ml-auto mr-2" />
            <button
              onClick={closeMenu}
              className="relative p-2 rounded-md text-neutral-700 hover:text-primary hover:bg-gray-100 transition-all duration-300"
              aria-label="Close menu"
            >
              <div className="relative w-6 h-6">
                <span className="absolute block h-0.5 w-6 bg-current transform rotate-45 translate-y-2.5 transition-transform duration-300" />
                <span className="absolute block h-0.5 w-6 bg-current transform -rotate-45 translate-y-2.5 transition-transform duration-300" />
              </div>
            </button>
          </div>

          {/* Main Content Area - Only scroll when needed */}
          <div className="flex-1 min-h-0">
            {activeMegaMenu ? (
              // Mega Menu Tabbed View
              <div className={`h-full overflow-y-auto transition-all duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
                {/* Back Button */}
                <div className="lp-mm-back sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
                  <button
                    onClick={closeMegaMenu}
                    className="flex items-center text-neutral-700 hover:text-primary transition-colors duration-200"
                  >
                    <ChevronLeft className="h-5 w-5 mr-2 transition-transform duration-200 hover:-translate-x-1" />
                    <span className="font-medium">{activeMegaMenu}</span>
                  </button>
                </div>

                {/* Tabbed Accordion Content */}
                {activeMegaMenu === 'Free Tools' && (
                  <div className="p-4">
                    <FreeToolsShowcase columns={(items.find((i) => i.label === 'Free Tools')?.columns ?? []) as any} onItemClick={closeMenu} variant="mobile" open={!isAnimating} />
                  </div>
                )}
                {items.map((item) => {
                  if (item.label === activeMegaMenu && item.label !== 'Free Tools' && item.type === 'mega' && item.columns) {
                    return (
                      <div key={item.label} className="p-4">
                        {/* Accordion Sections */}
                        {item.columns.map((column, columnIndex) => (
                          <div 
                            key={column.title} 
                            className={`mb-2 transition-all duration-300 delay-${columnIndex * 50}`}
                            style={{ transitionDelay: `${columnIndex * 50}ms` }}
                          >
                            <button
                              onClick={() => toggleSection(column.title)}
                              className={`lp-mm-sec w-full flex items-center justify-between ${isCompact ? 'p-3' : 'p-4'} rounded-lg font-medium transition-all duration-300 ${
                                expandedSection === column.title
                                  ? 'bg-gray-100 text-gray-900 shadow-sm'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <span>{column.title}</span>
                              <ChevronDown 
                                className={`h-5 w-5 transition-transform duration-300 ${
                                  expandedSection === column.title ? 'rotate-180' : ''
                                }`}
                              />
                            </button>
                            
                            {/* Expanded Section Content */}
                            <div 
                              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                expandedSection === column.title 
                                  ? 'max-h-[2000px] opacity-100' 
                                  : 'max-h-0 opacity-0'
                              }`}
                            >
                              <div className="pt-2 pb-4 px-4 space-y-2">
                                {column.items.map((subItem: any, itemIndex: number) => {
                                  const Icon = subItem.icon ? iconMap[subItem.icon as keyof typeof iconMap] : null;
                                  
                                  return (
                                    <Link
                                      key={subItem.href}
                                      href={subItem.href}
                                      onClick={closeMenu}
                                      className={`lp-mm-item flex items-start p-3 rounded-lg hover:bg-gray-50 transition-all duration-300 delay-${itemIndex * 30}`}
                                      style={{ 
                                        transitionDelay: expandedSection === column.title ? `${itemIndex * 30}ms` : '0ms',
                                        opacity: expandedSection === column.title ? 1 : 0,
                                        transform: expandedSection === column.title ? 'translateX(0)' : 'translateX(-10px)'
                                      }}
                                    >
                                      {Icon && (
                                        <Icon 
                                          className="w-5 h-5 text-[#294bfd] mt-0.5 mr-3 flex-shrink-0 transition-transform duration-200 hover:scale-110" 
                                          aria-hidden="true"
                                        />
                                      )}
                                      <div className="flex-1">
                                        <div className="font-medium text-gray-900">
                                          {subItem.label}
                                        </div>
                                        <div className="text-sm text-gray-600 mt-0.5">
                                          {subItem.description}
                                        </div>
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* CTA Section - Removed for mobile to reduce clutter */}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            ) : (
              // Main Navigation View - Determine if scrolling needed
              <nav className={`flex flex-col h-full transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
                <div className="overflow-y-auto flex-1">
                  <div className="p-4 space-y-2">
                {items.filter((item) => !item.desktopOnly).map((item, index) => (
                  <div 
                    key={item.label}
                    className="transition-all duration-300"
                    style={{ 
                      transitionDelay: `${index * 50}ms`,
                      opacity: isAnimating ? 0 : 1,
                      transform: isAnimating ? 'translateY(10px)' : 'translateY(0)'
                    }}
                  >
                    {item.type === 'mega' ? (
                      <button
                        onClick={() => openMegaMenu(item.label)}
                        className={`lp-mm-row flex items-center justify-between w-full ${isCompact ? 'py-3' : 'py-4'} px-4 text-left text-neutral-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-all duration-300 hover:scale-[1.02]`}
                      >
                        <span className="font-medium">{item.label}</span>
                        <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                      </button>
                    ) : (
                      <Link
                        href={item.href}
                        onClick={closeMenu}
                        className={`lp-mm-row block ${isCompact ? 'py-3' : 'py-4'} px-4 text-neutral-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-all duration-300 hover:scale-[1.02] font-medium`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
                  </div>
                </div>
              </nav>
            )}
          </div>

          {/* Main CTA Button - Separate container */}
          <div className="border-t border-gray-200 p-4">
            {startHere ? (
              <a href="#start" data-start-here="" aria-haspopup="dialog" onClick={closeMenu} className="lp-mm-cta block text-center py-3 px-6 bg-[#ff34a7] hover:bg-[#e6309a] text-white rounded-full font-semibold hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                Start Here Free
              </a>
            ) : (
            <Link
              href="/mortgage-lead-generation-demo"
              onClick={closeMenu}
              className="lp-mm-cta block text-center py-3 px-6 bg-[#ff34a7] hover:bg-[#e6309a] text-white rounded-full font-semibold hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
            >
              Book Strategy Call
            </Link>
            )}
            <TryItLink onClick={closeMenu} className="lp-tryit block text-center mt-3 py-2 text-[color:var(--sk-pinkt,#c8117f)] font-semibold hover:underline underline-offset-4" />
          </div>
        </div>
      </div>
    </div>
  );
}